/**
 * MonthlyPayout
 *
 * Admin panel to run the monthly bulk payout cycle.
 * POST /admin/referrals/approve-month with month + year.
 * Pays all referrers who have awaiting_payment rewards for that earning month.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { getRequest, postRequest } from '../../utils/httpRequest';
import { API_ENDPOINTS } from './constants';
import { colors, commonStyles, gradients } from '../AgenticPaymentTransactionHistory/styles';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const selectStyle = {
  padding: '10px 36px 10px 14px',
  border: `1px solid ${colors.gray400}`,
  borderRadius: '4px',
  fontSize: '14px',
  background: colors.white,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  backgroundSize: '12px',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  height: '42px',
  minWidth: '160px',
};

const payoutPrefBadge = (pref) => pref ? (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
    background: pref === 'local' ? '#d4edda' : '#cce5ff',
    color: pref === 'local' ? '#155724' : '#004085',
    textTransform: 'uppercase',
  }}>{pref}</span>
) : '-';

const subThStyle = {
  padding: '8px 12px', textAlign: 'left', color: colors.primary,
  fontWeight: 700, fontSize: '11px', textTransform: 'uppercase',
  letterSpacing: '0.4px', whiteSpace: 'nowrap',
  background: '#eef1f8', borderBottom: `2px solid ${colors.border}`,
};
const subTdStyle = {
  padding: '8px 12px', borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle', fontSize: '12px',
};

const PreviewGroupedTable = ({ referrals }) => {
  // Group by referrer
  const groups = [];
  const map = new Map();
  referrals.forEach((r) => {
    const key = r.referrer_user_id || r.referrer_email || 'unknown';
    if (!map.has(key)) {
      map.set(key, { referrer_email: r.referrer_email, referrals: [] });
      groups.push(map.get(key));
    }
    map.get(key).referrals.push(r);
  });

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: '6px', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr>
            {['Referrer / Referee', 'Country', 'Currency', 'Reward', 'Payout Pref'].map((h) => (
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', background: gradients.primary, color: '#fff', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `2px solid ${colors.accent}`, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const totalRaw = group.referrals.reduce((s, r) => s + (r.reward_amount_raw || 0), 0);
            const sym = group.referrals[0]?.reward_amount?.replace(/[\d.,\s]/g, '') || '';
            const totalFmt = sym
              ? `${sym}${totalRaw.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : totalRaw.toFixed(2);

            return [
              // Referrer header row
              <tr key={`hdr-${group.referrer_email}`} style={{ background: 'linear-gradient(135deg, #f0f3fa 0%, #e8ecf5 100%)' }}>
                <td style={{ padding: '10px 14px', fontWeight: 700, fontSize: '12px', color: colors.primary, borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, background: colors.primary, color: '#fff' }}>
                      {group.referrals.length} referral{group.referrals.length !== 1 ? 's' : ''}
                    </span>
                    {group.referrer_email}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}` }} />
                <td style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}` }} />
                <td style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}`, fontWeight: 700, fontSize: '12px', color: colors.gray700 }}>{totalFmt}</td>
                <td style={{ padding: '10px 14px', borderBottom: `1px solid ${colors.border}` }} />
              </tr>,
              // Sub-header
              <tr key={`subhdr-${group.referrer_email}`}>
                {['Referee', 'Country', 'Currency', 'Reward', 'Payout Pref'].map((h) => (
                  <th key={h} style={subThStyle}>{h}</th>
                ))}
              </tr>,
              // Referral rows
              ...group.referrals.map((r, idx) => (
                <tr key={r.id} style={{ background: idx % 2 === 0 ? '#fff' : colors.bgLight }}>
                  <td style={{ ...subTdStyle, paddingLeft: '24px' }}>{r.referee_name || '-'}</td>
                  <td style={subTdStyle}>{r.country || '-'}</td>
                  <td style={subTdStyle}>{r.reward_currency || '-'}</td>
                  <td style={subTdStyle}><strong>{r.reward_amount || '-'}</strong></td>
                  <td style={subTdStyle}>{payoutPrefBadge(r.payout_preference)}</td>
                </tr>
              )),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
};

const MonthlyPayout = ({ adminUserId }) => {
  const now          = new Date();
  // Default to previous month (that's what you'd typically be paying out)
  const prevMonth    = now.getMonth() === 0 ? 12 : now.getMonth();
  const prevYear     = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

  const [month, setMonth]           = useState(prevMonth);
  const [year, setYear]             = useState(prevYear);
  const [loading, setLoading]       = useState(false);
  const [result, setResult]         = useState(null);
  const [confirmed, setConfirmed]   = useState(false);
  const [previewCount, setPreviewCount]       = useState(null);
  const [previewLoading, setPreviewLoading]   = useState(false);
  const [previewReferrals, setPreviewReferrals] = useState([]);

  const fetchPreviewCount = useCallback(async (m, y) => {
    setPreviewCount(null);
    setPreviewReferrals([]);
    setPreviewLoading(true);
    try {
      const params = new URLSearchParams({ status: 'awaiting_payment', month: m, year: y, limit: 100 });
      const data = await getRequest(`${API_ENDPOINTS.LIST}?${params.toString()}`);
      if (data.success) {
        setPreviewCount(data.data?.total_count ?? 0);
        setPreviewReferrals(data.data?.referrals || []);
      } else {
        setPreviewCount(null);
      }
    } catch {
      setPreviewCount(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => { fetchPreviewCount(month, year); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMonthChange = (val) => {
    const m = Number(val);
    setMonth(m);
    setResult(null);
    setConfirmed(false);
    fetchPreviewCount(m, year);
  };

  const handleYearChange = (val) => {
    const y = Number(val);
    setYear(y);
    setResult(null);
    setConfirmed(false);
    fetchPreviewCount(month, y);
  };

  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-indexed
  const years        = Array.from({ length: currentYear - 2024 + 1 }, (_, i) => 2024 + i);
  const availableMonths = MONTHS.filter((_, i) => year < currentYear || (i + 1) <= currentMonth);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    setConfirmed(false);
    try {
      const data = await postRequest(
        `${API_ENDPOINTS.APPROVE_MONTH}?month=${month}&year=${year}`,
        { admin_user_id: adminUserId }
      );
      if (!data.success) throw new Error(data.error || 'Monthly payout failed');
      setResult({ success: true, data: data.data });
    } catch (err) {
      setResult({ success: false, error: err.message || 'Monthly payout failed' });
    } finally {
      setLoading(false);
    }
  };

  const monthLabel = MONTHS[month - 1];

  return (
    <div>
      {/* Panel */}
      <div style={{ ...commonStyles.panel }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          background: gradients.primary,
          borderBottom: `3px solid ${colors.accent}`,
          borderRadius: '6px 6px 0 0',
        }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#fff' }}>
            Monthly Bulk Payout
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.75)' }}>
            Pays all referrers with <strong style={{ color: '#fff' }}>awaiting_payment</strong> rewards for the selected earning month.
            Each referrer receives one transfer in their preferred currency.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: '28px 24px' }}>
          {/* Month / Year selectors */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.primary }}>
                Earning Month
              </label>
              <select value={month} onChange={(e) => handleMonthChange(e.target.value)} style={selectStyle}>
                {availableMonths.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.primary }}>
                Year
              </label>
              <select value={year} onChange={(e) => handleYearChange(e.target.value)} style={selectStyle}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Awaiting referrals preview */}
          <div style={{ marginBottom: '20px' }}>
            {previewLoading ? (
              <span style={{ fontSize: '13px', color: colors.gray500 }}>Checking awaiting referrals...</span>
            ) : previewCount === null ? null : previewCount === 0 ? (
              <span style={{ fontSize: '13px', color: '#856404', background: '#fff3cd', padding: '6px 14px', borderRadius: '4px', border: '1px solid #ffc107', fontWeight: 600 }}>
                No awaiting referrals for {MONTHS[month - 1]} {year}
              </span>
            ) : (
              <div>
                <div style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', color: '#155724', background: '#d4edda', padding: '6px 14px', borderRadius: '4px', border: '1px solid #c3e6cb', fontWeight: 600 }}>
                    {previewCount} awaiting referral{previewCount !== 1 ? 's' : ''} for {MONTHS[month - 1]} {year}
                  </span>
                </div>
                <PreviewGroupedTable referrals={previewReferrals} />
              </div>
            )}
          </div>

          {/* Confirmation step */}
          {!confirmed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ padding: '12px 20px', background: colors.gray100, borderRadius: '6px', border: `1px solid ${colors.border}`, fontSize: '14px', color: colors.gray700 }}>
                Run payout for <strong style={{ color: colors.primary }}>{monthLabel} {year}</strong>?
              </div>
              <button
                type="button"
                disabled={!previewCount || previewLoading}
                onClick={() => setConfirmed(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 28px',
                  height: '42px',
                  lineHeight: '1',
                  background: (!previewCount || previewLoading) ? colors.gray300 : gradients.primary,
                  color: (!previewCount || previewLoading) ? colors.gray500 : '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 700,
                  fontSize: '14px',
                  cursor: (!previewCount || previewLoading) ? 'not-allowed' : 'pointer',
                  letterSpacing: '0.2px',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  boxShadow: 'none',
                  textShadow: 'none',
                }}
              >
                Run Payout
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ padding: '12px 20px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107', fontSize: '14px', color: '#7d5a00', fontWeight: 600 }}>
                ⚠ This will trigger on-chain transfers. Confirm to proceed for <strong>{monthLabel} {year}</strong>.
              </div>
              <button
                type="button"
                disabled={loading}
                onClick={handleRun}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 28px', height: '42px', lineHeight: '1',
                  background: loading ? colors.gray300 : 'linear-gradient(135deg, #28a745, #218838)',
                  color: loading ? colors.gray500 : '#fff',
                  border: 'none', borderRadius: '4px', fontWeight: 700, fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '0.2px',
                  WebkitAppearance: 'none', appearance: 'none', boxShadow: 'none', textShadow: 'none',
                }}
              >
                {loading ? 'Processing...' : 'Confirm & Pay'}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => setConfirmed(false)}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 20px', height: '42px', lineHeight: '1',
                  background: colors.gray200, color: colors.gray700,
                  border: 'none', borderRadius: '4px', fontWeight: 600, fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  WebkitAppearance: 'none', appearance: 'none', boxShadow: 'none', textShadow: 'none',
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ marginTop: '24px' }}>
              {result.success ? (
                <div style={{ padding: '20px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#155724', fontSize: '15px' }}>
                    ✓ Payout complete for {monthLabel} {year}
                  </p>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ padding: '10px 18px', background: '#fff', borderRadius: '4px', border: '1px solid #c3e6cb', fontSize: '13px' }}>
                      <span style={{ color: colors.gray600 }}>Referrers processed: </span>
                      <strong style={{ color: colors.primary }}>{result.data?.referrers_processed ?? '-'}</strong>
                    </div>
                    <div style={{ padding: '10px 18px', background: '#fff', borderRadius: '4px', border: '1px solid #c3e6cb', fontSize: '13px' }}>
                      <span style={{ color: colors.gray600 }}>Total referrals paid: </span>
                      <strong style={{ color: colors.primary }}>{result.data?.total_paid ?? '-'}</strong>
                    </div>
                  </div>
                  {result.data?.results?.length > 0 && (
                    <div style={{ marginTop: '16px', overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr>
                            {['Referrals Paid', 'Currency', 'Amount', 'TX ID', 'Paid At'].map((h) => (
                              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', background: colors.primary, color: '#fff', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `2px solid ${colors.accent}` }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.results.map((r, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : colors.bgLight }}>
                              <td style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}` }}>{r.referrals_paid}</td>
                              <td style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}` }}>{r.reward_currency}</td>
                              <td style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}` }}><strong>{r.total_amount_formatted}</strong></td>
                              <td style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}` }}>
                                <code style={{ fontSize: '11px', fontFamily: 'monospace', background: colors.gray200, padding: '2px 6px', borderRadius: '3px' }}>{r.payment_tx_id || '-'}</code>
                              </td>
                              <td style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}`, whiteSpace: 'nowrap' }}>{r.paid_at ? new Date(r.paid_at).toLocaleString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding: '16px 20px', background: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#721c24', fontSize: '14px' }}>{result.error}</span>
                  <button type="button" onClick={() => setResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: '#721c24', fontSize: '16px' }}>✕</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyPayout;
